import React, { useContext, useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Button, Switch, Text, TextInput } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';

import JobsContext from '../context/JobsContext';
import { JobAlert } from '../types';
import { Palette, radius, spacing, useThemePalette } from '../theme';
import { track } from '../lib/analytics';
import { jobMatchesAlert } from '../lib/alerts/match';
import useAuthUser from '../lib/auth/useAuthUser';
import notifications from '../lib/notifications';
import AdBanner from '../components/AdBanner';

const AlertCard: React.FC<{ alert: JobAlert }> = ({ alert }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const { data, toggleAlert, deleteAlert } = useContext(JobsContext);

  // How many jobs currently in the feed match this alert — instant feedback.
  const matching = useMemo(
    () => data.filter((job) => jobMatchesAlert(job, alert)).length,
    [data, alert]
  );

  const label = [alert.query, ...(alert.tags ?? [])].filter(Boolean).join(' · ');

  return (
    <View style={[styles.card, !alert.enabled && styles.cardDisabled]}>
      <View style={styles.cardRow}>
        <View style={styles.cardText}>
          <Text style={styles.query} numberOfLines={2}>
            {label}
          </Text>
          <TouchableOpacity
            onPress={() =>
              router.push({
                pathname: '/',
                params: { q: alert.query || (alert.tags ?? []).join(' ') },
              })
            }
            disabled={matching === 0}
          >
            <Text style={matching > 0 ? styles.matchingLink : styles.matching}>
              {t('alerts.matchingNow', { count: matching })}
              {matching > 0 ? ' ›' : ''}
            </Text>
          </TouchableOpacity>
        </View>
        <Switch
          value={alert.enabled}
          onValueChange={() => {
            toggleAlert(alert.id);
            track('alert_toggle', { enabled: !alert.enabled });
          }}
          color={palette.primary}
        />
        <TouchableOpacity
          onPress={() => {
            deleteAlert(alert.id);
            track('alert_delete');
          }}
          hitSlop={8}
          style={styles.deleteBtn}
          accessibilityLabel={t('applications.remove')}
        >
          <MaterialCommunityIcons name="trash-can-outline" size={20} color={palette.danger} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default function AlertsScreen() {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const { alerts, addAlert } = useContext(JobsContext);
  const user = useAuthUser();
  const [query, setQuery] = useState('');

  const submit = () => {
    if (!query.trim()) return;
    addAlert(query);
    track('alert_create');
    setQuery('');
    // The user just asked to be notified — request permission and register the
    // push token now (no-op until messaging is installed / a uid exists).
    notifications.registerForPush(user?.uid);
  };

  return (
    <SafeAreaView style={styles.safeArea} edges={['top']}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{t('alerts.title')}</Text>
        </View>

        <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
          <Text style={styles.subtitle}>{t('alerts.subtitle')}</Text>

          <View style={styles.createRow}>
            <TextInput
              mode="outlined"
              value={query}
              onChangeText={setQuery}
              placeholder={t('alerts.placeholder')}
              autoCapitalize="none"
              onSubmitEditing={submit}
              returnKeyType="done"
              style={styles.input}
            />
            <Button
              mode="contained"
              onPress={submit}
              disabled={!query.trim()}
              buttonColor={palette.primary}
              textColor="#0F1115"
              style={styles.addBtn}
              contentStyle={styles.addBtnContent}
            >
              {t('common.add')}
            </Button>
          </View>

          {alerts.length === 0 ? (
            <View style={styles.empty}>
              <MaterialCommunityIcons name="bell-outline" size={40} color={palette.textMuted} />
              <Text style={styles.emptyText}>{t('alerts.empty')}</Text>
            </View>
          ) : (
            alerts.map((alert) => <AlertCard key={alert.id} alert={alert} />)
          )}
        </ScrollView>
        <AdBanner />
      </View>
    </SafeAreaView>
  );
}

const isWeb = Platform.OS === 'web';

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: p.canvas },
    container: {
      flex: 1,
      width: '100%',
      maxWidth: isWeb ? 720 : undefined,
      alignSelf: isWeb ? 'center' : undefined,
      backgroundColor: p.canvas,
    },
    header: { paddingHorizontal: spacing.lg, paddingTop: spacing.md, paddingBottom: spacing.sm },
    headerTitle: { color: p.textPrimary, fontSize: 18, fontWeight: '700' },
    content: { padding: spacing.lg, paddingBottom: spacing.xxl, gap: spacing.md },
    subtitle: { fontSize: 14, color: p.textSecondary },
    createRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    input: { flex: 1, backgroundColor: p.surface },
    addBtn: { borderRadius: radius.pill },
    addBtnContent: { minHeight: 48 },
    card: {
      backgroundColor: p.surface,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: p.border,
      padding: spacing.md,
    },
    cardDisabled: { opacity: 0.55 },
    cardRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    cardText: { flex: 1 },
    query: { fontSize: 14, fontWeight: '600', color: p.textPrimary },
    matching: { fontSize: 12, color: p.textSecondary, marginTop: 2 },
    matchingLink: { fontSize: 12, color: p.secondary, marginTop: 2, fontWeight: '600' },
    deleteBtn: { padding: spacing.xs },
    empty: { alignItems: 'center', justifyContent: 'center', padding: spacing.xl, gap: spacing.sm },
    emptyText: { fontSize: 14, color: p.textSecondary, textAlign: 'center' },
  });
